interface DateAdherenceData {
  customer: string
  supplier: string
  month: string
  adherence: number // 0-100 percentage
  total: number // total PO lines
  onTime: number // on-time deliveries
}

interface DateAdherenceKPIChartProps {
  data?: DateAdherenceData[]
  onCellClick?: (data: DateAdherenceData) => void
}

// Sample data generation removed — use real API data only

// Get color based on adherence percentage
const getColor = (adherence: number): string => {
  if (adherence >= 95) return '#10b981' // green-500
  if (adherence >= 85) return '#84cc16' // lime-500
  if (adherence >= 75) return '#eab308' // yellow-500
  if (adherence >= 60) return '#f97316' // orange-500
  return '#ef4444' // red-500
}

// Get text color based on background
const getTextColor = (adherence: number): string => {
  if (adherence >= 85 || adherence < 60) return '#ffffff'
  return '#000000'
}

export function DateAdherenceKPIChart({ data, onCellClick }: DateAdherenceKPIChartProps) {
  const chartData = data || []

  // Extract unique values for axes
  const suppliers = Array.from(new Set(chartData.map(d => d.supplier)))
  const months = Array.from(new Set(chartData.map(d => d.month)))

  // Create a lookup map for quick access
  const dataMap = new Map<string, DateAdherenceData>()
  for (const d of chartData) {
    dataMap.set(`${d.customer}-${d.supplier}-${d.month}`, d)
  }

  // Calculate averages for each supplier-month combination
  const supplierMonthData = suppliers.map(supplier => {
    return months.map(month => {
      const cells = chartData.filter(d => d.supplier === supplier && d.month === month)
      const avgAdherence = cells.length > 0
        ? Math.round(cells.reduce((sum, c) => sum + c.adherence, 0) / cells.length)
        : 0
      const totalLines = cells.reduce((sum, c) => sum + c.total, 0)
      const onTimeLines = cells.reduce((sum, c) => sum + c.onTime, 0)
      return { supplier, month, adherence: avgAdherence, total: totalLines, onTime: onTimeLines }
    })
  })

  return (
    <div className="w-full overflow-x-auto">
      {/* Legend */}
      <div className="flex items-center justify-end gap-4 mb-3 text-xs">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded" style={{ backgroundColor: '#10b981' }}></span>
          {'>=95%'}
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded" style={{ backgroundColor: '#84cc16' }}></span>
          {'85-94%'}
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded" style={{ backgroundColor: '#eab308' }}></span>
          {'75-84%'}
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded" style={{ backgroundColor: '#f97316' }}></span>
          {'60-74%'}
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 rounded" style={{ backgroundColor: '#ef4444' }}></span>
          {'<60%'}
        </span>
      </div>

      {/* Heatmap Table */}
      <table className="w-full border-collapse text-xs">
        <thead>
          <tr>
            <th className="p-2 text-left font-medium text-muted-foreground border-b">Supplier</th>
            {months.map(month => (
              <th key={month} className="p-2 text-center font-medium text-muted-foreground border-b">
                {month}
              </th>
            ))}
            <th className="p-2 text-center font-medium text-muted-foreground border-b">Avg</th>
          </tr>
        </thead>
        <tbody>
          {supplierMonthData.map((row, rowIdx) => {
            const supplier = suppliers[rowIdx]
            const avgAdherence = Math.round(row.reduce((sum, c) => sum + c.adherence, 0) / row.length)

            return (
              <tr key={supplier} className="hover:bg-muted/50">
                <td className="p-2 font-medium border-b">{supplier}</td>
                {row.map((cell, colIdx) => (
                  <td
                    key={`${supplier}-${months[colIdx]}`}
                    className="p-1 text-center border-b cursor-pointer transition-transform hover:scale-110"
                    onClick={() => onCellClick && onCellClick(cell as DateAdherenceData)}
                    title={`${cell.adherence}% - ${cell.onTime}/${cell.total} lines on time`}
                  >
                    <div
                      className="rounded p-2 font-semibold"
                      style={{
                        backgroundColor: getColor(cell.adherence),
                        color: getTextColor(cell.adherence),
                      }}
                    >
                      {cell.adherence}%
                    </div>
                  </td>
                ))}
                <td className="p-1 text-center border-b">
                  <div
                    className="rounded p-2 font-semibold"
                    style={{
                      backgroundColor: getColor(avgAdherence),
                      color: getTextColor(avgAdherence),
                    }}
                  >
                    {avgAdherence}%
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>

      {/* Summary Row */}
      <div className="mt-4 flex justify-between items-center text-sm">
        <div className="text-muted-foreground">
          Click on a cell to drill down into delivery details
        </div>
        <div className="flex gap-4">
          <span className="text-muted-foreground">
            Total Suppliers: <strong>{suppliers.length}</strong>
          </span>
          <span className="text-muted-foreground">
            Periods: <strong>{months.length}</strong>
          </span>
        </div>
      </div>
    </div>
  )
}

export default DateAdherenceKPIChart
